/**
 * Channel Sidebar - Slack-like channel list
 */

import React, { useState, useEffect } from 'react';
import { List, Input, Button, Badge, Avatar, Typography, Empty, Modal, Form, Select, Radio, message, Dropdown, Menu, ColorPicker, Tag, Spin, Tooltip } from 'antd';
import { TagOutlined, UserOutlined, PlusOutlined, SearchOutlined, MessageOutlined, StarOutlined, StarFilled, FolderOutlined, FolderAddOutlined, MoreOutlined, EditOutlined, DeleteOutlined, WechatOutlined, AppstoreAddOutlined, LockOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { PresenceIndicator } from './PresenceIndicator';
import { UserStatusBar } from './UserStatusBar';
import { ThemeSelector } from './ThemeSelector';
import './ChannelSidebar.css';

const { Text } = Typography;

type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

// Helper function to ensure status is valid
const normalizeStatus = (status: string | undefined): PresenceStatus => {
  if (status === 'online' || status === 'away' || status === 'busy' || status === 'offline') {
    return status;
  }
  return 'offline';
};

interface Channel {
  id: string;
  name: string;
  type: 'group' | 'direct' | 'private';
  unread_count?: number;
  unreadCount?: number;
  last_message_content?: string;
  lastMessageContent?: string;
  last_message_at?: string;
  lastMessageAt?: string;
  created_at?: string;
  createdAt?: string;
  is_starred?: boolean;
  isStarred?: boolean;
  is_private?: boolean;
  isPrivate?: boolean;
  group_id?: string;
  groupId?: string;
  other_user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    presence_status?: string;
  };
  otherUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    presenceStatus?: string;
  };
}

interface CustomGroup {
  id: string;
  name: string;
  color?: string;
  sort_order: number;
}

interface ChannelSidebarProps {
  selectedChannelId: string | null;
  onChannelSelect: (channelId: string) => void;
  onSearchClick?: () => void;
}

export const ChannelSidebar: React.FC<ChannelSidebarProps> = ({
  selectedChannelId,
  onChannelSelect,
  onSearchClick,
}) => {
  const { presence } = useChatWebSocket();
  const [searchQuery, setSearchQuery] = useState('');
  const [createChannelVisible, setCreateChannelVisible] = useState(false);
  const [startDMVisible, setStartDMVisible] = useState(false);
  const [searchPublicVisible, setSearchPublicVisible] = useState(false);
  const [publicSearchQuery, setPublicSearchQuery] = useState('');
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [startingDM, setStartingDM] = useState(false);
  const [joiningChannel, setJoiningChannel] = useState<string | null>(null);
  const [createChannelForm] = Form.useForm();
  const [startDMForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: channelsData, isLoading } = useQuery(
    'chat-channels',
    () => api.get('/api/chat/channels').then(res => res.data),
    {
      refetchInterval: 10000, // Refetch every 10 seconds
    }
  );

  const { data: usersData } = useQuery(
    'chat-users',
    () => api.get('/api/chat/users').then(res => res.data),
    {
      enabled: startDMVisible,
    }
  );

  // Fetch custom groups
  const { data: groupsData } = useQuery(
    'chat-custom-groups',
    () => api.get('/api/chat/groups').then(res => res.data),
  );

  // Fetch public channels
  const { data: publicChannelsData, isLoading: loadingPublicChannels, refetch: refetchPublicChannels } = useQuery(
    ['public-channels', publicSearchQuery],
    () => {
      const searchParam = publicSearchQuery.trim() ? `?search=${encodeURIComponent(publicSearchQuery.trim())}` : '';
      return api.get(`/api/chat/channels/public${searchParam}`).then(res => res.data);
    },
    {
      enabled: searchPublicVisible,
      refetchOnWindowFocus: false,
    }
  );

  // Refetch when modal opens
  useEffect(() => {
    if (searchPublicVisible) {
      refetchPublicChannels();
    }
  }, [searchPublicVisible, refetchPublicChannels]);

  const groups = groupsData?.groups || [];
  const [favoritingChannel, setFavoritingChannel] = useState<string | null>(null);
  const [createGroupVisible, setCreateGroupVisible] = useState(false);
  const [createGroupForm] = Form.useForm();

  const handleJoinPublicChannel = async (channelId: string) => {
    setJoiningChannel(channelId);
    try {
      await api.post(`/api/chat/channels/${channelId}/join`);
      message.success('Joined channel successfully');
      queryClient.invalidateQueries('chat-channels');
      refetchPublicChannels();
      onChannelSelect(channelId);
      setSearchPublicVisible(false);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to join channel');
    } finally {
      setJoiningChannel(null);
    }
  };

  const handleToggleFavorite = async (channelId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavoritingChannel(channelId);
    try {
      const response = await api.put(`/api/chat/channels/${channelId}/favorite`);
      const isStarred = response.data.is_starred || response.data.isStarred;
      message.success(isStarred ? 'Added to favorites' : 'Removed from favorites');
      // Invalidate and immediately refetch to update the sidebar
      await queryClient.invalidateQueries('chat-channels');
      await queryClient.refetchQueries('chat-channels');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update favorite');
    } finally {
      setFavoritingChannel(null);
    }
  };

  const handleCreateGroup = async (values: any) => {
    try {
      await api.post('/api/chat/groups', {
        name: values.name,
        color: values.color?.toHexString?.() || values.color,
      });
      message.success('Group created successfully');
      queryClient.invalidateQueries('chat-custom-groups');
      setCreateGroupVisible(false);
      createGroupForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to create group');
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await api.delete(`/api/chat/groups/${groupId}`);
      message.success('Group deleted successfully');
      queryClient.invalidateQueries('chat-custom-groups');
      queryClient.invalidateQueries('chat-channels');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete group');
    }
  };

  const handleAddToGroup = async (channelId: string, groupId: string) => {
    try {
      await api.post(`/api/chat/groups/${groupId}/channels`, { channelId });
      message.success('Channel added to group');
      queryClient.invalidateQueries('chat-channels');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to add channel to group');
    }
  };

  const handleRemoveFromGroup = async (channelId: string, groupId: string) => {
    try {
      await api.delete(`/api/chat/groups/${groupId}/channels/${channelId}`);
      message.success('Channel removed from group');
      queryClient.invalidateQueries('chat-channels');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to remove channel from group');
    }
  };


  const handleCreateChannel = async (values: any) => {
    if (creatingChannel) return; // Prevent double submission
    
    try {
      setCreatingChannel(true);
      console.log('[Create Channel] Submitting:', values);
      
      // Ensure isPrivate is a boolean
      const isPrivate = values.isPrivate === true || values.isPrivate === 'true';
      
      if (!values.name || !values.name.trim()) {
        message.error('Channel name is required');
        setCreatingChannel(false);
        return;
      }
      
      const response = await api.post('/api/chat/channels', {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        isPrivate: isPrivate,
      });
      
      console.log('[Create Channel] Response:', response.data);
      message.success('Channel created successfully');
      setCreateChannelVisible(false);
      createChannelForm.resetFields();
      await queryClient.invalidateQueries('chat-channels');
      
      // Select the new channel
      if (response.data.channel) {
        onChannelSelect(response.data.channel.id);
      }
    } catch (error: any) {
      console.error('[Create Channel] Error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create channel';
      message.error(errorMessage);
    } finally {
      setCreatingChannel(false);
    }
  };

  const handleStartDM = async (values: any) => {
    try {
      console.log('[Start DM] Submitting:', values);
      const response = await api.post('/api/chat/channels/direct', {
        userId: values.userId,
      });
      
      console.log('[Start DM] Response:', response.data);
      message.success('Conversation started');
      setStartDMVisible(false);
      startDMForm.resetFields();
      queryClient.invalidateQueries('chat-channels');
      
      // Select the new DM channel
      if (response.data.channel) {
        onChannelSelect(response.data.channel.id);
      }
    } catch (error: any) {
      console.error('[Start DM] Error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to start conversation';
      message.error(errorMessage);
    }
  };

  const channels: Channel[] = channelsData?.channels || [];

  // Helper to get other user (handles both snake_case and camelCase)
  const getOtherUser = (channel: Channel) => {
    if (channel.other_user) {
      return {
        id: channel.other_user.id,
        firstName: channel.other_user.first_name,
        lastName: channel.other_user.last_name,
        email: channel.other_user.email,
        presenceStatus: channel.other_user.presence_status,
      };
    }
    return channel.otherUser;
  };

  const filteredChannels = channels.filter(channel => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (channel.type === 'direct') {
      const otherUser = getOtherUser(channel);
      if (otherUser) {
        return `${otherUser.firstName} ${otherUser.lastName}`.toLowerCase().includes(query);
      }
    }
    return channel.name.toLowerCase().includes(query);
  });

  // Separate direct messages and channels
  const directMessages = filteredChannels.filter(ch => ch.type === 'direct');
  const channelsOnly = filteredChannels.filter(ch => ch.type !== 'direct'); // Include both 'group' and 'private' channels

  // Organize channels by groups and favorites
  const organizeChannels = (channelsToOrganize: Channel[]) => {
    const starred: Channel[] = [];
    const grouped: { [key: string]: Channel[] } = {};
    const ungrouped: Channel[] = [];

    channelsToOrganize.forEach((channel: Channel) => {
      const isStarred = channel.is_starred || channel.isStarred;
      const groupId = channel.group_id || channel.groupId;

      if (isStarred) {
        starred.push(channel);
      } else if (groupId) {
        if (!grouped[groupId]) {
          grouped[groupId] = [];
        }
        grouped[groupId].push(channel);
      } else {
        ungrouped.push(channel);
      }
    });

    // Sort starred items by last message time (most recent first)
    starred.sort((a, b) => {
      const aTime = a.last_message_at || a.lastMessageAt || a.created_at || a.createdAt || '';
      const bTime = b.last_message_at || b.lastMessageAt || b.created_at || b.createdAt || '';
      return bTime.localeCompare(aTime);
    });

    return { starred, grouped, ungrouped };
  };

  const { starred: starredDMs, grouped: groupedDMs, ungrouped: ungroupedDMs } = organizeChannels(directMessages);
  const { starred: starredChannels, grouped: groupedChannels, ungrouped: ungroupedChannels } = organizeChannels(channelsOnly);
  
  // Combine all favorited items for the top "Favorite" section
  const allFavorites = [...starredDMs, ...starredChannels].sort((a, b) => {
    const aTime = a.last_message_at || a.lastMessageAt || a.created_at || a.createdAt || '';
    const bTime = b.last_message_at || b.lastMessageAt || b.created_at || b.createdAt || '';
    return bTime.localeCompare(aTime);
  });
  
  // Filter out favorited items from their original sections
  const nonStarredDMs = directMessages.filter(ch => !(ch.is_starred || ch.isStarred));
  const nonStarredChannels = channelsOnly.filter(ch => !(ch.is_starred || ch.isStarred));
  
  // Reorganize non-starred items
  const { starred: _starredDMs, grouped: groupedDMsNonStarred, ungrouped: ungroupedDMsNonStarred } = organizeChannels(nonStarredDMs);
  const { starred: _starredChannels, grouped: groupedChannelsNonStarred, ungrouped: ungroupedChannelsNonStarred } = organizeChannels(nonStarredChannels);

  const getChannelDisplayName = (channel: Channel) => {
    if (channel.type === 'direct') {
      const otherUser = getOtherUser(channel);
      if (otherUser) {
        const firstName = otherUser.firstName || '';
        const lastName = otherUser.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || otherUser.email || 'Unknown User';
      }
    }
    return channel.name || 'Unnamed Channel';
  };

  const getChannelIcon = (channel: Channel) => {
    if (channel.type === 'direct') {
      const otherUser = getOtherUser(channel);
      if (otherUser) {
        const userPresence = presence[otherUser.id] || null;
        const status: PresenceStatus = userPresence?.status 
          ? normalizeStatus(userPresence.status)
          : normalizeStatus(otherUser.presenceStatus);
        return (
          <PresenceIndicator
            status={status}
            size="small"
          >
            <Avatar size="small" icon={<UserOutlined />} />
          </PresenceIndicator>
        );
      }
    }
    // Use different icons for private vs public channels
    const isPrivate = (channel as any).is_private || (channel as any).isPrivate;
    if (isPrivate) {
      return <LockOutlined className="channel-icon" style={{ color: '#ff4d4f' }} />;
    }
    return <TagOutlined className="channel-icon" style={{ color: '#52c41a' }} />;
  };

  return (
    <div className="channel-sidebar">
      {/* User Status Bar at Top */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e4e7ea', background: '#ffffff' }}>
        <UserStatusBar />
        <ThemeSelector />
      </div>

      {/* Header */}
      <div className="channel-sidebar-header">
        <div className="channel-sidebar-search" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Input
            placeholder="Search channels..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            style={{ flex: 1, margin: '8px 0' }}
          />
          {onSearchClick && (
            <Tooltip title="Search messages & files (Ctrl+K)">
              <Button
                type="text"
                icon={<SearchOutlined />}
                onClick={onSearchClick}
                size="small"
                style={{ marginRight: '8px' }}
              />
            </Tooltip>
          )}
        </div>
        <Button
          type="link"
          size="small"
          onClick={() => setSearchPublicVisible(true)}
          style={{ width: 'calc(100% - 16px)', margin: '0 8px 8px 8px', padding: '4px 8px' }}
        >
          Browse Public Channels
        </Button>
      </div>

      {/* Create Channel Modal */}
      <Modal
        title="Create Channel"
        open={createChannelVisible}
        onCancel={() => {
          setCreateChannelVisible(false);
          createChannelForm.resetFields();
        }}
        footer={null}
      >
        <Form form={createChannelForm} onFinish={handleCreateChannel} layout="vertical">
          <Form.Item
            name="name"
            label="Channel Name"
            rules={[{ required: true, message: 'Please enter a channel name' }]}
          >
            <Input placeholder="e.g. general" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description (optional)"
          >
            <Input.TextArea rows={2} placeholder="What's this channel about?" />
          </Form.Item>
          <Form.Item
            name="isPrivate"
            label="Channel Type"
            initialValue={false}
          >
            <Radio.Group>
              <Radio value={false}>Public - Anyone can join</Radio>
              <Radio value={true}>Private - Only invited members</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              block 
              loading={creatingChannel}
              disabled={creatingChannel}
            >
              Create Channel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Start DM Modal */}
      <Modal
        title="Start a Conversation"
        open={startDMVisible}
        onCancel={() => {
          setStartDMVisible(false);
          startDMForm.resetFields();
        }}
        footer={null}
      >
        <Form form={startDMForm} onFinish={handleStartDM} layout="vertical">
          <Form.Item
            name="userId"
            label="Select User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Search for a user..."
              showSearch
              filterOption={(input, option) => {
                const label = typeof option?.label === 'string' ? option.label : String(option?.label || '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              options={usersData?.users?.map((user: any) => {
                const firstName = user.first_name || user.firstName || '';
                const lastName = user.last_name || user.lastName || '';
                const email = user.email || '';
                return {
                  value: user.id,
                  label: `${firstName} ${lastName}`.trim() || email || 'Unknown User',
                };
              }) || []}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Start Conversation
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Search Public Channels Modal */}
      <Modal
        title="Browse Public Channels"
        open={searchPublicVisible}
        onCancel={() => {
          setSearchPublicVisible(false);
          setPublicSearchQuery('');
        }}
        footer={null}
        width={600}
      >
        <Input
          placeholder="Search public channels..."
          prefix={<SearchOutlined />}
          value={publicSearchQuery}
          onChange={(e) => setPublicSearchQuery(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        {loadingPublicChannels ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <List
            dataSource={publicChannelsData?.channels || []}
            renderItem={(channel: any) => (
            <List.Item
              actions={[
                <Button
                  key="join"
                  type="primary"
                  size="small"
                  loading={joiningChannel === channel.id}
                  onClick={() => handleJoinPublicChannel(channel.id)}
                  disabled={channel.is_member}
                >
                  {channel.is_member ? 'Joined' : 'Join'}
                </Button>
              ]}
            >
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {channel.is_private ? (
                      <LockOutlined style={{ color: '#ff4d4f' }} />
                    ) : (
                      <TagOutlined style={{ color: '#52c41a' }} />
                    )}
                    <Text strong>{channel.name}</Text>
                    {channel.is_private && <Tag color="red">Private</Tag>}
                  </div>
                }
                description={
                  <div>
                    {channel.description && <Text type="secondary">{channel.description}</Text>}
                    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                      {channel.member_count || 0} {channel.member_count === 1 ? 'member' : 'members'}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
          />
        )}
        {!loadingPublicChannels && (!publicChannelsData?.channels || publicChannelsData.channels.length === 0) && (
          <Empty description="No public channels found" style={{ marginTop: 40 }} />
        )}
      </Modal>

      {/* Channel List */}
      <div className="channel-list">
        {isLoading ? (
          <div style={{ padding: 16, textAlign: 'center' }}>Loading...</div>
        ) : (
          <>
            {/* Favorite Section - Combined favorites at the top */}
            {allFavorites.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ 
                  padding: '8px 16px', 
                  fontSize: 12, 
                  fontWeight: 600, 
                  color: '#9ca2a8', 
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>Favorite</span>
                </div>
                {allFavorites.map((channel) => (
                  <List.Item
                    key={channel.id}
                    className={`channel-item ${selectedChannelId === channel.id ? 'active' : ''}`}
                    onClick={() => onChannelSelect(channel.id)}
                    style={{ cursor: 'pointer', padding: 0, paddingLeft: 16 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                      {getChannelIcon(channel)}
                      <Text
                        ellipsis
                        style={{
                          flex: 1,
                          marginLeft: 8,
                          fontWeight: selectedChannelId === channel.id ? 600 : 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        {getChannelDisplayName(channel)}
                      </Text>
                      <Button
                        type="text"
                        icon={(channel.is_starred || channel.isStarred) ? <StarFilled /> : <StarOutlined />}
                        onClick={(e) => handleToggleFavorite(channel.id, e)}
                        loading={favoritingChannel === channel.id}
                        style={{ color: '#f5a623', padding: '4px 8px', flexShrink: 0 }}
                        size="small"
                      />
                      {(channel.unreadCount || channel.unread_count || 0) > 0 && (
                        <Badge count={channel.unreadCount || channel.unread_count || 0} size="small" style={{ flexShrink: 0, marginLeft: 4 }} />
                      )}
                    </div>
                  </List.Item>
                ))}
              </div>
            )}

            {/* Direct Messages Section */}
            {(nonStarredDMs.length > 0 || true) && (
              <div style={{ marginTop: allFavorites.length > 0 ? 16 : 8 }}>
                <div style={{ 
                  padding: '8px 16px', 
                  fontSize: 12, 
                  fontWeight: 600, 
                  color: '#9ca2a8', 
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>Direct Messages</span>
                  <Tooltip title="Start a conversation">
                    <Button
                      type="text"
                      icon={<WechatOutlined />}
                      onClick={() => setStartDMVisible(true)}
                      style={{ color: '#9ca2a8', border: 'none', padding: 0, height: 'auto' }}
                      size="small"
                    />
                  </Tooltip>
                </div>

                {/* Grouped DMs */}
                {groups.map((group: CustomGroup) => {
                  const groupDMs = groupedDMsNonStarred[group.id] || [];
                  if (groupDMs.length === 0) return null;

                  return (
                    <div key={`dm-group-${group.id}`} style={{ marginTop: 4 }}>
                      <div style={{ 
                        padding: '8px 16px', 
                        fontSize: 11, 
                        fontWeight: 600, 
                        color: '#9ca2a8', 
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        paddingLeft: 24
                      }}>
                        <FolderOutlined style={{ color: group.color || '#9ca2a8' }} />
                        <span>{group.name}</span>
                      </div>
                      {groupDMs.map((channel) => (
                        <List.Item
                          key={channel.id}
                          className={`channel-item ${selectedChannelId === channel.id ? 'active' : ''}`}
                          onClick={() => onChannelSelect(channel.id)}
                          style={{ cursor: 'pointer', padding: 0, paddingLeft: 40 }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                            {getChannelIcon(channel)}
                            <Text
                              ellipsis
                              style={{
                                flex: 1,
                                marginLeft: 8,
                                fontWeight: selectedChannelId === channel.id ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                            >
                              {getChannelDisplayName(channel)}
                            </Text>
                            <Button
                              type="text"
                              icon={(channel.is_starred || channel.isStarred) ? <StarFilled /> : <StarOutlined />}
                              onClick={(e) => handleToggleFavorite(channel.id, e)}
                              loading={favoritingChannel === channel.id}
                              style={{ color: '#f5a623', padding: '4px 8px', flexShrink: 0 }}
                              size="small"
                            />
                            {(channel.unreadCount || channel.unread_count || 0) > 0 && (
                              <Badge count={channel.unreadCount || channel.unread_count || 0} size="small" style={{ flexShrink: 0, marginLeft: 4 }} />
                            )}
                          </div>
                        </List.Item>
                      ))}
                    </div>
                  );
                })}

                {/* Ungrouped DMs */}
                {ungroupedDMsNonStarred.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {ungroupedDMsNonStarred.map((channel) => (
                      <List.Item
                        key={channel.id}
                        className={`channel-item ${selectedChannelId === channel.id ? 'active' : ''}`}
                        onClick={() => onChannelSelect(channel.id)}
                        style={{ cursor: 'pointer', padding: 0, paddingLeft: 16 }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                          {getChannelIcon(channel)}
                          <Text
                            ellipsis
                            style={{
                              flex: 1,
                              marginLeft: 8,
                              fontWeight: selectedChannelId === channel.id ? 600 : 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}
                          >
                            {getChannelDisplayName(channel)}
                          </Text>
                          <Button
                            type="text"
                            icon={(channel.is_starred || channel.isStarred) ? <StarFilled /> : <StarOutlined />}
                            onClick={(e) => handleToggleFavorite(channel.id, e)}
                            loading={favoritingChannel === channel.id}
                            style={{ color: '#f5a623', padding: '4px 8px', flexShrink: 0 }}
                            size="small"
                          />
                          {(channel.unreadCount || channel.unread_count || 0) > 0 && (
                            <Badge count={channel.unreadCount || channel.unread_count || 0} size="small" style={{ flexShrink: 0, marginLeft: 4 }} />
                          )}
                        </div>
                      </List.Item>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Channels Section */}
            {(nonStarredChannels.length > 0 || true) && (
              <div style={{ marginTop: 16 }}>
                <div style={{ 
                  padding: '8px 16px', 
                  fontSize: 12, 
                  fontWeight: 600, 
                  color: '#9ca2a8', 
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>Channels</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Tooltip title="Create a channel">
                      <Button
                        type="text"
                        icon={<AppstoreAddOutlined />}
                        onClick={() => setCreateChannelVisible(true)}
                        style={{ color: '#9ca2a8', border: 'none', padding: 0, height: 'auto' }}
                        size="small"
                      />
                    </Tooltip>
                    <Tooltip title="Create a group">
                      <Button
                        type="text"
                        icon={<FolderAddOutlined />}
                        onClick={() => setCreateGroupVisible(true)}
                        style={{ color: '#9ca2a8', border: 'none', padding: 0, height: 'auto' }}
                        size="small"
                      />
                    </Tooltip>
                  </div>
                </div>

                {/* Custom Groups for Channels */}
                {groups.map((group: CustomGroup) => {
                  const groupChannels = groupedChannelsNonStarred[group.id] || [];
                  if (groupChannels.length === 0) return null;

                  return (
                    <div key={`channel-group-${group.id}`} style={{ marginTop: 4 }}>
                      <div style={{ 
                        padding: '8px 16px', 
                        fontSize: 11, 
                        fontWeight: 600, 
                        color: '#9ca2a8', 
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        paddingLeft: 24
                      }}>
                        <FolderOutlined style={{ color: group.color || '#9ca2a8' }} />
                        <span>{group.name}</span>
                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: 'delete',
                                icon: <DeleteOutlined />,
                                label: 'Delete Group',
                                danger: true,
                                onClick: () => handleDeleteGroup(group.id),
                              },
                            ],
                          }}
                          trigger={['click']}
                        >
                          <Button
                            type="text"
                            icon={<MoreOutlined />}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            style={{ marginLeft: 'auto', padding: 0, height: 'auto' }}
                          />
                        </Dropdown>
                      </div>
                      {groupChannels.map((channel) => (
                        <List.Item
                          key={channel.id}
                          className={`channel-item ${selectedChannelId === channel.id ? 'active' : ''}`}
                          onClick={() => onChannelSelect(channel.id)}
                          style={{ cursor: 'pointer', padding: 0, paddingLeft: 40 }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                            {getChannelIcon(channel)}
                            <Text
                              ellipsis
                              style={{
                                flex: 1,
                                marginLeft: 8,
                                fontWeight: selectedChannelId === channel.id ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                            >
                              {getChannelDisplayName(channel)}
                            </Text>
                            <Button
                              type="text"
                              icon={(channel.is_starred || channel.isStarred) ? <StarFilled /> : <StarOutlined />}
                              onClick={(e) => handleToggleFavorite(channel.id, e)}
                              loading={favoritingChannel === channel.id}
                              style={{ color: '#f5a623', padding: '4px 8px', flexShrink: 0 }}
                              size="small"
                            />
                            {(channel.unreadCount || channel.unread_count || 0) > 0 && (
                              <Badge count={channel.unreadCount || channel.unread_count || 0} size="small" style={{ flexShrink: 0, marginLeft: 4 }} />
                            )}
                          </div>
                        </List.Item>
                      ))}
                    </div>
                  );
                })}

                {/* Ungrouped Channels */}
                {ungroupedChannelsNonStarred.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {ungroupedChannelsNonStarred.map((channel) => (
                      <List.Item
                        key={channel.id}
                        className={`channel-item ${selectedChannelId === channel.id ? 'active' : ''}`}
                        onClick={() => onChannelSelect(channel.id)}
                        style={{ cursor: 'pointer', padding: 0, paddingLeft: 16 }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                          {getChannelIcon(channel)}
                          <Text
                            ellipsis
                            style={{
                              flex: 1,
                              marginLeft: 8,
                              fontWeight: selectedChannelId === channel.id ? 600 : 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}
                          >
                            {getChannelDisplayName(channel)}
                          </Text>
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: 'favorite',
                                  icon: (channel.is_starred || channel.isStarred) ? <StarFilled /> : <StarOutlined />,
                                  label: (channel.is_starred || channel.isStarred) ? 'Remove from favorites' : 'Add to favorites',
                                  onClick: () => handleToggleFavorite(channel.id, { stopPropagation: () => {} } as any),
                                },
                                {
                                  key: 'addToGroup',
                                  icon: <FolderAddOutlined />,
                                  label: 'Add to group',
                                  children: groups.map((group: CustomGroup) => ({
                                    key: `group-${group.id}`,
                                    label: group.name,
                                    onClick: () => handleAddToGroup(channel.id, group.id),
                                  })),
                                },
                              ],
                            }}
                            trigger={['click']}
                          >
                            <Button
                              type="text"
                              icon={<MoreOutlined />}
                              onClick={(e) => e.stopPropagation()}
                              style={{ padding: '4px 8px', flexShrink: 0 }}
                              size="small"
                            />
                          </Dropdown>
                          {(channel.unreadCount || channel.unread_count || 0) > 0 && (
                            <Badge count={channel.unreadCount || channel.unread_count || 0} size="small" style={{ flexShrink: 0, marginLeft: 4 }} />
                          )}
                        </div>
                      </List.Item>
                    ))}
                  </div>
                )}
              </div>
            )}

            {directMessages.length === 0 && channelsOnly.length === 0 && (
              <Empty description="No channels" style={{ marginTop: 40 }} />
            )}
          </>
        )}
      </div>

      {/* Create Custom Group Modal */}
      <Modal
        title="Create Custom Group"
        open={createGroupVisible}
        onCancel={() => {
          setCreateGroupVisible(false);
          createGroupForm.resetFields();
        }}
        footer={null}
      >
        <Form form={createGroupForm} onFinish={handleCreateGroup} layout="vertical">
          <Form.Item
            name="name"
            label="Group Name"
            rules={[{ required: true, message: 'Please enter a group name' }]}
          >
            <Input placeholder="e.g. Team Projects" />
          </Form.Item>
          <Form.Item
            name="color"
            label="Color (optional)"
          >
            <ColorPicker showText />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Create Group
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

